import React, { useEffect, useState, useRef } from 'react';
import styles from './index.module.scss';
import { getImageFromS3 } from '../../../imageService';
import HeadContainer from '../../../common/head-container';
import classNames from 'classnames';
import InputMask from 'react-input-mask';
import apiService from '../../../utils/apiService';
import brandMatchingUtils from '../../../utils/brandMatchingUtils';

const popularDomains = [
  'gmail.com',
  'ukr.net',
  'yahoo.com',
  'outlook.com',
  'i.ua',
  'meta.ua'
];

const FormScreen = ({
  params,
  setScreenNumber,
  backButtonClick,
  telegramLink,
  setFormData,
  formData,
  onTelegramHandleClick
}) => {
  const utm_source = sessionStorage.getItem('utm_source');
  const utm_source_id = sessionStorage.getItem('utm_source_id');
  const [name, setName] = useState(formData?.name ?? '');
  const [nameError, setNameError] = useState(false);
  const [email, setEmail] = useState(formData?.email ?? '');
  const [emailError, setEmailError] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState(false);
  const [isOpenBrandSelect, setIsOpenBrandSelect] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(
    formData?.selectedBrand ?? null
  );
  const [selectedBrandError, setSelectedBrandError] = useState(false);
  const [isOpenFormatSelect, setIsOpenFormatSelect] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState(
    formData?.selectedFormat ?? null
  );
  const [selectedFormatError, setSelectedFormatError] = useState(false);
  const [brandsList, setBrandsList] = useState([]);
  const [formatsList, setFormatsList] = useState([]);
  const [isCheckedPolicy, setIsCheckedPolicy] = useState(
    formData?.isCheckedPolicy ?? false
  );
  const [isCheckedPolicyError, setIsCheckedPolicyError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmailDomains, setShowEmailDomains] = useState(false);
  const [emailUser, setEmailUser] = useState('');
  const emailInputRef = useRef(null);
  const emailDomainsRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiService.get('api/cda/brands');

        const sortedBrandList = data.sort((a, b) => {
          if (
            a.public_description?.toLowerCase() === 'інші' &&
            b.public_description?.toLowerCase() !== 'інші'
          )
            return 1;
          if (
            a.public_description?.toLowerCase() !== 'інші' &&
            b.public_description?.toLowerCase() === 'інші'
          )
            return -1;
          return 0;
        });

        setBrandsList(sortedBrandList);
      } catch (e) {
        setScreenNumber(500);
      }
    })();

    const handleClickOutside = (event) => {
      if (
        emailDomainsRef.current &&
        !emailDomainsRef.current.contains(event.target) &&
        emailInputRef.current &&
        !emailInputRef.current.contains(event.target)
      ) {
        setShowEmailDomains(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const sanitizeName = (v) => (v || '').replace(/[^A-Za-zА-Яа-яІЇії’ \-]/g, '');
  const sanitizeEmail = (v) => (v || '').replace(/[<>\/\\"' ;(){}\[\]=`]/g, '');

  const handleEmailChange = (e) => {
    const rawValue = e.target.value;
    const value = sanitizeEmail(rawValue);
    setEmail(value);

    if (value.includes('@')) {
      const [user, domain] = value.split('@');
      setEmailUser(user);

      setShowEmailDomains(true);
    } else {
      setEmailUser(value);
      setShowEmailDomains(false);
    }
  };

  const handleNameChange = (e) => {
    const value = sanitizeName(e.target.value);
    setName(value);
  };

  const selectDomain = (domain) => {
    setEmail(`${emailUser}@${domain}`);
    setShowEmailDomains(false);
  };

  const handleCheckBeforeSubmit = () => {
    const emailValid = handleCheckEmail();
    const phoneValid = handleCheckPhone();
    const validBrand = !!selectedBrand;
    const validFormat = !!selectedFormat;

    if (!emailValid) setEmailError(true);
    if (!phoneValid) setPhoneError(true);
    if (!validBrand) setSelectedBrandError(true);
    if (!validFormat) setSelectedFormatError(true);
    if (!isCheckedPolicy) setIsCheckedPolicyError(true);

    return (
      emailValid && phoneValid && validBrand && validFormat && isCheckedPolicy
    );
  };

  const handleCheckEmail = (emailValue = null) => {
    const currentEmail = emailValue?.length > 0 ? emailValue : email;
    if (currentEmail?.length === 0) {
      setEmailError(false);
      return true;
    }

    if (
      currentEmail?.length > 320 ||
      currentEmail.includes('..') ||
      currentEmail.startsWith('.') ||
      currentEmail.endsWith('.') ||
      currentEmail.includes('.@') ||
      currentEmail.includes('@.')
    ) {
      setEmailError(true);
      return false;
    }

    const forbiddenCharsRegex = /[\s\(\)\[\]\{\}<>:"'#$%&*+=?^`|~]/;
    if (forbiddenCharsRegex.test(currentEmail)) {
      setEmailError(true);
      return false;
    }

    const parts = currentEmail.split('@');
    if (parts.length !== 2) {
      setEmailError(true);
      return false;
    }

    const [localPart, domainPart] = parts;

    if (localPart.length < 1 || localPart.length > 64) {
      setEmailError(true);
      return false;
    }

    if (domainPart.length < 3 || domainPart.length > 253) {
      setEmailError(true);
      return false;
    }

    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (
      currentEmail?.length > 0 &&
      !emailRegex.test(currentEmail) &&
      !showEmailDomains
    )
      setEmailError(true);

    return currentEmail?.length > 0 ? emailRegex.test(currentEmail) : true;
  };

  const handleCheckPhone = () => {
    const regex = /^380\d{9}$/;
    const updatedPhone = phone.replace(/\D/g, '').replace(/^380/, '380');

    if (!regex.test('380' + updatedPhone)) setPhoneError(true);

    return regex.test('380' + updatedPhone);
  };

  const handleSubmit = async () => {
    const windowPathname = sessionStorage.getItem('pathname');
    const windowSearch = sessionStorage.getItem('search');
    const isQrCode = sessionStorage.getItem('isQrCode');
    const utm_campaign = sessionStorage.getItem('utm_campaign');
    const uaBrand = brandMatchingUtils.brandKeys.find((key) =>
      isQrCode?.toLowerCase().includes(key.toLowerCase())
    );
    const isQrCodeMatchingStructure =
      !!uaBrand &&
      windowPathname + windowSearch ===
        `/${uaBrand}?utm_campaign=${utm_campaign}`;
    const utm_partner = sessionStorage.getItem('utm_partner');
    const normalizedSourceId =
      utm_source === 'HOSTESS' && utm_source_id?.startsWith('ActInf')
        ? utm_source_id.slice(6)
        : utm_source_id;

    if (isSubmitting) return;

    if (handleCheckBeforeSubmit()) {
      try {
        setIsSubmitting(true);
        const newUser = await apiService.post('api/cda/users', {
          data: {
            name: name?.length > 0 ? name : null,
            phone: '+380' + phone.replace(/\D/g, ''),
            email: email?.length > 0 ? email : null,
            brandParentId: selectedBrand?.id,
            brandChildId: selectedFormat?.id,
            sourceId: normalizedSourceId,
            sourceType: utm_source,
            ...(utm_source === 'HOSTESS'
              ? { hostessId: normalizedSourceId }
              : {}),
            ...(isQrCodeMatchingStructure
              ? { channelRefCode: 'JUSTSCAN' }
              : {}),
            ...(utm_partner ? { partner: utm_partner } : {})
          }
        });

        sessionStorage.setItem('u_i', newUser?.data?.id);
        sessionStorage.setItem('p_n', '+380' + phone.replace(/\D/g, ''));
        setFormData({
          name: name?.length > 0 ? name : '',
          selectedBrand,
          selectedFormat,
          phone: '+380' + phone.replace(/\D/g, ''),
          email: email?.length > 0 ? email : null,
          isCheckedPolicy: true
        });
        setScreenNumber(3);
      } catch (e) {
        const code = e?.response?.data?.code;

        if (code === 100007) {
          setScreenNumber(4);
          return;
        }

        setScreenNumber(500);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div
      className={styles.container}
      style={{
        backgroundImage: `url("${getImageFromS3('images/ua-hostess/bg.png')}")`
      }}
    >
      <div className={styles.headContainer}>
        <HeadContainer
          setScreenNumber={setScreenNumber}
          telegramOnly
          customTelegramLink={telegramLink}
          onTelegramHandleClick={onTelegramHandleClick}
        />
      </div>
      <p className={styles.title}>
        Заповни, <br></br>будь ласка свої дані
      </p>
      <div className={styles.textInputContainer}>
        <p className={styles.inputLabel}>Ім'я</p>
        <input
          type="name"
          value={name}
          onChange={handleNameChange}
          onFocus={() => setNameError(false)}
          // onBlur={(event) => {
          //   //if (event.target.value?.length === 0) setNameError(true);
          // }}
          placeholder="Введіть своє ім'я"
          required
          className={classNames(nameError && styles.errorInput)}
        />
        {/*{nameError && <p className={styles.error}>Обов'язкове поле</p>}*/}
      </div>
      <div className={styles.phoneInputContainer}>
        <div className={styles.labelContainer}>
          <p>Код</p>
          <p>Номер телефону*</p>
        </div>
        <div className={styles.inputContainer}>
          <div className={styles.codeContainer}>+380</div>
          <InputMask
            mask="(99) 999-99-99"
            maskChar="_"
            alwaysShowMask
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onFocus={() => setPhoneError(false)}
            onBlur={handleCheckPhone}
            className={classNames(
              styles.input,
              phoneError && styles.errorInput
            )}
          >
            {(inputProps) => <input {...inputProps} type="tel" />}
          </InputMask>
          {phoneError && (
            <i className={styles.phoneErrorIcon}>
              <svg
                width="28"
                height="28"
                viewBox="0 0 28 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g filter="url(#filter0_d_0_76)">
                  <circle cx="14" cy="10" r="10" fill="#F9524D" />
                  <path
                    d="M14.9444 5L14.7407 12.555H13.2593L13.0494 5H14.9444ZM13 14.894C13 14.5752 13.0905 14.3111 13.2716 14.1019C13.4568 13.8877 13.6996 13.7806 14 13.7806C14.3045 13.7806 14.5473 13.8877 14.7284 14.1019C14.9095 14.3111 15 14.5752 15 14.894C15 15.2029 14.9095 15.4644 14.7284 15.6787C14.5473 15.8929 14.3045 16 14 16C13.6996 16 13.4568 15.8929 13.2716 15.6787C13.0905 15.4644 13 15.2029 13 14.894Z"
                    fill="white"
                  />
                </g>
                <defs>
                  <filter
                    id="filter0_d_0_76"
                    x="0"
                    y="0"
                    width="28"
                    height="28"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                  >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                      in="SourceAlpha"
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                      result="hardAlpha"
                    />
                    <feOffset dy="4" />
                    <feGaussianBlur stdDeviation="2" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                    />
                    <feBlend
                      mode="normal"
                      in2="BackgroundImageFix"
                      result="effect1_dropShadow_0_76"
                    />
                    <feBlend
                      mode="normal"
                      in="SourceGraphic"
                      in2="effect1_dropShadow_0_76"
                      result="shape"
                    />
                  </filter>
                </defs>
              </svg>
            </i>
          )}
        </div>
        {phoneError && (
          <p className={styles.error}>Перевір коректність вказаного номеру</p>
        )}
      </div>
      <div
        className={styles.textInputContainer}
        style={{ position: 'relative' }}
      >
        <p className={styles.inputLabel}>Email</p>
        <input
          ref={emailInputRef}
          type="email"
          value={email}
          onChange={handleEmailChange}
          onFocus={() => setEmailError(false)}
          onBlur={(e) => {
            if (!showEmailDomains) handleCheckEmail();
          }}
          placeholder="Введіть свій e-mail(необов'язково)"
          required
          className={classNames(emailError && styles.errorInput)}
        />
        {emailError && !showEmailDomains && (
          <i className={styles.errorIcon}>
            <svg
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g filter="url(#filter0_d_0_76)">
                <circle cx="14" cy="10" r="10" fill="#F9524D" />
                <path
                  d="M14.9444 5L14.7407 12.555H13.2593L13.0494 5H14.9444ZM13 14.894C13 14.5752 13.0905 14.3111 13.2716 14.1019C13.4568 13.8877 13.6996 13.7806 14 13.7806C14.3045 13.7806 14.5473 13.8877 14.7284 14.1019C14.9095 14.3111 15 14.5752 15 14.894C15 15.2029 14.9095 15.4644 14.7284 15.6787C14.5473 15.8929 14.3045 16 14 16C13.6996 16 13.4568 15.8929 13.2716 15.6787C13.0905 15.4644 13 15.2029 13 14.894Z"
                  fill="white"
                />
              </g>
              <defs>
                <filter
                  id="filter0_d_0_76"
                  x="0"
                  y="0"
                  width="28"
                  height="28"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="4" />
                  <feGaussianBlur stdDeviation="2" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_0_76"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_dropShadow_0_76"
                    result="shape"
                  />
                </filter>
              </defs>
            </svg>
          </i>
        )}
        {showEmailDomains && email.includes('@') && (
          <div ref={emailDomainsRef} className={styles.emailDomainsList}>
            {popularDomains.map((domain, index) => (
              <div
                key={index}
                onClick={() => {
                  selectDomain(domain);
                  setEmail(`${emailUser}@${domain}`);
                  setEmailError(!handleCheckEmail(`${emailUser}@${domain}`));
                }}
              >
                {`${emailUser}@${domain}`}
              </div>
            ))}
          </div>
        )}
        {emailError && !showEmailDomains && (
          <p className={styles.error}>Перевір коректність вказаного e-mail</p>
        )}
      </div>
      <p style={{ marginTop: 60 }} className={styles.title}>
        Обери свій варіант із<br></br> випадаючого списку
      </p>
      <p style={{ marginTop: 24 }} className={styles.inputLabel}>
        Основний бренд сигарет, які ти куриш?*
      </p>
      <div
        className={classNames(
          styles.selectContainer,
          selectedBrandError && styles.errorInput
        )}
        onClick={() => {
          setIsOpenFormatSelect(false);
          setIsOpenBrandSelect(!isOpenBrandSelect);
          setSelectedBrandError(false);
          setShowEmailDomains(false);
        }}
      >
        <p
          onClick={() => {
            setIsOpenFormatSelect(false);
            setIsOpenBrandSelect(!isOpenBrandSelect);
            setShowEmailDomains(false);
          }}
        >
          {selectedBrand ? selectedBrand?.public_description : 'Оберіть бренд'}
        </p>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="9"
          viewBox="0 0 13 9"
          fill="none"
        >
          <path
            d="M7.27406 8.05362C6.87395 8.5428 6.12605 8.5428 5.72594 8.05362L0.474528 1.63311C-0.0595353 0.980156 0.405036 6.31416e-07 1.24859 7.05162e-07L11.7514 1.62335e-06C12.595 1.69709e-06 13.0595 0.980156 12.5255 1.63311L7.27406 8.05362Z"
            fill="#461B77"
          />
        </svg>
        {isOpenBrandSelect && (
          <div className={styles.list}>
            {brandsList.map((brand, index) => (
              <p
                key={index?.toString()}
                onClick={() => {
                  setSelectedBrand(brand);
                  setSelectedFormat(null);
                  setFormatsList(brand?.children);
                  setSelectedBrandError(false);
                }}
                className={classNames(
                  brand === selectedBrand && styles.selectedItem
                )}
              >
                {brand?.public_description}
              </p>
            ))}
          </div>
        )}
      </div>
      {selectedBrandError && <p className={styles.error}>Обов'язкове поле</p>}
      <p style={{ marginTop: 31 }} className={styles.inputLabel}>
        Сигарети якого формату ти обираєш?*
      </p>
      <div
        className={classNames(
          styles.selectContainer,
          selectedFormatError && styles.errorInput
        )}
        onClick={() => {
          if (formatsList?.length > 0)
            setIsOpenFormatSelect(!isOpenFormatSelect);
          setIsOpenBrandSelect(false);
          setSelectedFormatError(false);
          setShowEmailDomains(false);
        }}
      >
        <p
          onClick={() => {
            if (formatsList?.length > 0)
              setIsOpenFormatSelect(!isOpenFormatSelect);
            setIsOpenBrandSelect(false);
            setSelectedFormatError(false);
            setShowEmailDomains(false);
          }}
        >
          {selectedFormat
            ? selectedFormat?.public_description
            : 'Оберіть формат'}
        </p>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="9"
          viewBox="0 0 13 9"
          fill="none"
        >
          <path
            d="M7.27406 8.05362C6.87395 8.5428 6.12605 8.5428 5.72594 8.05362L0.474528 1.63311C-0.0595353 0.980156 0.405036 6.31416e-07 1.24859 7.05162e-07L11.7514 1.62335e-06C12.595 1.69709e-06 13.0595 0.980156 12.5255 1.63311L7.27406 8.05362Z"
            fill="#461B77"
          />
        </svg>
        {isOpenFormatSelect && (
          <div className={styles.list}>
            {formatsList.map((format, index) => (
              <p
                key={index?.toString()}
                onClick={() => {
                  setSelectedFormat(format);
                  setSelectedFormatError(false);
                }}
                className={classNames(
                  format === selectedFormat && styles.selectedItem
                )}
              >
                {format?.public_description}
              </p>
            ))}
          </div>
        )}
      </div>
      {selectedFormatError && <p className={styles.error}>Обов'язкове поле</p>}
      <div
        className={styles.privacyPolicy}
        onClick={() => {
          setShowEmailDomains(false);
        }}
      >
        <input
          type="checkbox"
          id="privacy"
          name="privacy"
          onClick={() => {
            setIsCheckedPolicy(!isCheckedPolicy);
            setIsCheckedPolicyError(false);
          }}
          checked={isCheckedPolicy}
        />
        <span
          className={classNames(
            styles.checkmark,
            isCheckedPolicyError && styles.errorInput
          )}
        ></span>
        <label htmlFor="privacy">
          Я погоджуюсь з{' '}
          <a
            href="https://yourjourney.com.ua/personal-data-policy"
            target="_blank"
          >
            положенням про конфіденційність та захист персональних даних
          </a>{' '}
          та підтверджую, що є споживачем тютюнових виробів та/або користувачем
          пристроїв для споживання тютюнових виробів без їх згоряння
        </label>
      </div>
      {isCheckedPolicyError && <p className={styles.error}>Обов'язкове поле</p>}
      <div
        className={styles.buttonContainer}
        onClick={() => {
          setShowEmailDomains(false);
        }}
      >
        <button
          className={styles.sendButton}
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting && <span className={styles.spinner} />}
          <span>Надіслати</span>
        </button>
        <p className={styles.backButton} onClick={backButtonClick}>
          Назад
        </p>
      </div>
    </div>
  );
};

export default FormScreen;
